import { List, Alert, AccordionProps } from '@mui/material'
import { Asset } from '../types'
import { useTranslation } from 'react-i18next'
import { ReactNode } from 'react'
import AssetDetailsList, {
  AssetAttachedAtListItem,
  AssetDetachedAtListItem,
  AssetPhotoListItem
} from './AssetDetailsList'
import AssetField from '../types/AssetField'
import CardAccordion from './CardAccordion'

interface AssetDetailsProps extends Omit<AccordionProps, 'expanded' | 'children' | 'onChange'> {
  asset: Asset | null
  fields?: AssetField[]
  editable?: boolean
  assetDetailsExtra?: ReactNode
  children?: ReactNode
  onUpdate?: (asset: Asset) => void
}

export default function AssetDetails({
  asset,
  fields = [
    'name',
    'company.name',
    'project.name',
    'level.name',
    'lift.name',
    'photo',
    'attachedAt',
    'detachedAt'
  ],
  editable = true,
  assetDetailsExtra,
  onUpdate,
  children,
  ...props
}: AssetDetailsProps) {
  const { t } = useTranslation()

  return (
    <CardAccordion title={t('assetInformation')} {...props}>
      <>
        {asset ? (
          <>
            <AssetDetailsList
              asset={asset}
              fields={fields.filter((field) =>
                ['name', 'company.name', 'project.name', 'level.name', 'lift.name'].includes(field)
              )}
              editable={editable}
              disablePadding
              afterUpdate={onUpdate}
            />

            {assetDetailsExtra}

            <List disablePadding>
              {fields.includes('photo') && (
                <AssetPhotoListItem
                  key="photo"
                  asset={asset}
                  editable={editable}
                  onUpdate={onUpdate}
                />
              )}

              {fields.includes('attachedAt') && (
                <AssetAttachedAtListItem
                  key="attachedAt"
                  asset={asset}
                  editable={editable}
                  afterSubmit={onUpdate}
                />
              )}

              {fields.includes('detachedAt') && (
                <AssetDetachedAtListItem
                  key="detachedAt"
                  asset={asset}
                  editable={editable}
                  afterSubmit={onUpdate}
                />
              )}
            </List>
          </>
        ) : (
          <Alert variant="outlined" severity="error" sx={{ border: 0 }}>
            {t('assetNotFound')}
          </Alert>
        )}

        {children}
      </>
    </CardAccordion>
  )
}
